import { useCallback, useEffect, useRef, useState } from 'react';

import { Api } from '../../api/api';
import type { ApiError } from '../../api/errors';
import { SessionMachine } from '../../session/sessionMachine';
import type { TaskState } from '../../session/sessionState';
import { canSubmit, currentTask, isClosed, sessionStateFrom } from '../../session/sessionState';
import { reportAccessDeniedInChildMode } from '../ChildHome/useChildHome';

import type { SessionScreenState } from './SessionState';

/**
 * How many hints the button assumes a task has before the server says otherwise.
 *
 * Optimistic on purpose: the count is only known once a hint is asked for, and offering a button that
 * turns out to have nothing behind it costs a child one tap and an honest sentence. Hiding a button that
 * did have a hint behind it costs them the help.
 */
const HINTS_PER_TASK = 2;

/**
 * Drives one training session.
 * Mirrors android/…/Screens/Session/SessionViewModel.kt.
 *
 * Every decision about what the session *means* — whether an answer closes a task, whether a replacement
 * arrives, when the session is over — belongs to `SessionMachine`. This hook only calls it, holds the
 * result, and measures how long the child had the task on screen.
 *
 * That measured time is sent as `clientReportedTimeMs` and is **diagnostic only**. The server measures the
 * real thing from its own clock, so a slow machine, a backgrounded tab or a tampered client cannot
 * influence a child's level (ADR-0004).
 */
export function useSession() {
  const [state, setState] = useState<SessionScreenState>({
    isLoading: true,
    isSubmitting: false,
    session: null,
    error: null,
    submitError: null,
    submitErrorWasOffline: false,
    isFinished: false,
    hint: null,
    hintsRemaining: HINTS_PER_TASK,
  });

  const taskShownAt = useRef<number>(0);

  const fail = useCallback((error: ApiError, context: string, keepTask: boolean = false): void => {
    // A child must never be shown a parent's error, and a 403 here is a routing defect rather than
    // something that happened to them (ADR-0006).
    if (error.kind === 'accessDenied') {
      reportAccessDeniedInChildMode(context);
      setState((current) => ({
        ...current,
        isLoading: false,
        error: null,
        submitError: null,
        submitErrorWasOffline: false,
      }));

      return;
    }

    // `keepTask` decides which of the two fields it lands in, and therefore whether the screen is
    // replaced or the task stays put (R0116).
    setState((current) =>
      keepTask
        ? {
            ...current,
            isLoading: false,
            submitError: error.message,
            submitErrorWasOffline: error.offline === true,
          }
        : { ...current, isLoading: false, error: error.message },
    );
  }, []);

  const start = useCallback(async (): Promise<void> => {
    setState((current) => ({ ...current, isLoading: true, error: null }));

    const result = await Api.startDailyTraining();

    if (result.ok) {
      taskShownAt.current = Date.now();
      setState((current) => ({ ...current, isLoading: false, session: sessionStateFrom(result.value) }));

      return;
    }

    fail(result.error, 'start');
  }, [fail]);

  useEffect(() => {
    void start();
  }, [start]);

  const mapCurrent = (transform: (task: TaskState) => TaskState): void =>
    setState((current) => {
      const session = current.session;

      if (session === null) return current;

      const task = currentTask(session);

      if (task === null) return current;

      const tasks = [...session.tasks];

      tasks[session.currentIndex] = transform(task);

      return { ...current, session: { ...session, tasks } };
    });

  const submit = useCallback(async (): Promise<void> => {
    const session = state.session;

    if (session === null) return;

    const task = currentTask(session);

    if (task === null || !canSubmit(task) || state.isSubmitting) return;

    const elapsed = Math.max(0, Date.now() - taskShownAt.current);
    const answer = SessionMachine.answerFor(task, elapsed);

    setState((current) => ({
      ...current,
      isSubmitting: true,
      error: null,
      submitError: null,
      submitErrorWasOffline: false,
    }));

    const result = await Api.submitAnswer(answer);

    if (result.ok) {
      setState((current) => {
        const updated =
          current.session === null ? null : SessionMachine.applyOutcome(current.session, result.value);

        return {
          ...current,
          isSubmitting: false,
          session: updated,
          isFinished: updated?.isComplete === true,
          // The answer's own feedback takes over the slot; a stale asked-for hint beside it would be
          // two pieces of help about two different moments.
          hint: null,
        };
      });

      return;
    }

    setState((current) => ({ ...current, isSubmitting: false }));
    // The task, and the child's selection, stay exactly as they are.
    fail(result.error, 'submit', true);
  }, [state, fail]);

  /**
   * design-system/06-Components/08-Lists-And-States.md §5, second half — a session in progress
   * **re-submits when connectivity returns** (R0120).
   *
   * R0116 delivered the first half: the task stays on screen, so the child's work is not thrown away.
   * This is what happens next. The answer is still selected and still valid, so the child pressing
   * „Skontrolovať" a second time would send exactly what this sends — the only difference is that they
   * do not have to notice the network came back, which a seven-year-old will not.
   *
   * Only after an **offline** failure. A 500 is not waiting for the wifi, and re-sending an answer the
   * server already rejected once, unprompted, would be a second attempt the child did not make.
   */
  useEffect(() => {
    if (state.submitError === null || !state.submitErrorWasOffline) return;

    const resend = (): void => void submit();

    window.addEventListener('online', resend);

    return () => window.removeEventListener('online', resend);
  }, [state.submitError, state.submitErrorWasOffline, submit]);

  return {
    state,
    start,

    select: (optionKey: string): void => mapCurrent((task) => SessionMachine.select(task, optionKey)),
    type: (text: string): void => mapCurrent((task) => SessionMachine.type(task, text)),
    reorder: (ordering: string[]): void => mapCurrent((task) => SessionMachine.reorder(task, ordering)),

    /**
     * Ask for a hint, before answering.
     *
     * The reply is rendered through the same feedback slot a wrong answer uses, so a child sees help in
     * one place however they came by it. The task stays answerable — asking is not an attempt (R0076).
     */
    requestHint: async (): Promise<void> => {
      const session = state.session;

      if (session === null || state.isSubmitting) return;

      const task = currentTask(session);

      if (task === null || task.phase === 'solved' || task.phase === 'closed') return;

      const result = await Api.requestHint(task.item.instanceToken);

      if (result.ok) {
        setState((current) => ({
          ...current,
          hint: result.value.message,
          hintsRemaining: result.value.hintsRemaining,
        }));

        return;
      }

      fail(result.error, 'hint');
    },

    submit,

    /** Moves to the next unfinished task, and restarts the client-side stopwatch for it. */
    next: (): void =>
      setState((current) => {
        // A failure from the previous task must not follow the child to the next one.
        const session = current.session;

        if (session === null) return current;

        const advanced = SessionMachine.advance(session);

        taskShownAt.current = Date.now();

        // Hint state belongs to a task, not to the session. Without this reset the first task to run out
        // of hints retired the button for every task after it (R0076).
        return {
          ...current,
          session: advanced,
          isFinished: advanced.tasks.every(isClosed),
          hint: null,
          hintsRemaining: HINTS_PER_TASK,
        };
      }),
  };
}
